import { create } from "zustand";

import {
  allowedWhileTyping,
  isTyping,
  resolveHotkey,
  type HotkeyAction,
  type TargetInfo,
} from "../lib/hotkeys";

/**
 * Слушатель горячих клавиш и «боевой режим».
 *
 * Слушатель НЕ хук: он вешается на document один раз на вкладку и возвращает
 * снималку — та же форма, что у startSocket() и startPendingWatch() в App.tsx.
 * Хук, смонтированный вместе с компонентом, течёт в StrictMode и гоняется с
 * фокусом; кроме того, функцию можно проверить в .ts-тесте под jsdom, а хук —
 * нельзя, потому что .tsx в vitest не входит.
 *
 * # Боевой режим
 *
 * Хоткеи по умолчанию НЕ отправляют ордера. Доводы за немедленную отправку
 * известны (веха про задержку, и лишний режим — это лишний шаг), но здесь
 * решает одно соображение: везде в системе от ошибки защищает трейдер, а тут
 * защищать нечем. Ордер размером с пресет по цене вершины стакана при снятом
 * стопе — это ровно тот ордер, который риск-гейт обязан ОДОБРИТЬ. Хоткей —
 * единственное место в системе без второй линии обороны, и умолчания проекта
 * везде запретительные (RISK_KILL_SWITCH=true, пустой справочник рынков
 * запрещает всё).
 *
 * Асимметрия та же, что у аварийного стопа, но повёрнутая в опасную сторону:
 * ВКЛЮЧИТЬ режим можно только мышью, выключить — одной клавишей.
 *
 * Режим НЕ сохраняется между перезагрузками: страница, которую только что
 * открыли, — это страница, на которой оператор ещё не сориентировался.
 */

/** Через сколько бездействия боевой режим гаснет сам. */
export const ARM_TIMEOUT_MS = 5 * 60 * 1000;

interface HotkeysStore {
  armed: boolean;
  /** performance.now() последнего боевого нажатия. Для авто-выключения. */
  lastActionAt: number;
  helpOpen: boolean;

  arm: () => void;
  disarm: () => void;
  toggleHelp: (open?: boolean) => void;
  noteAction: () => void;
}

export const useHotkeys = create<HotkeysStore>((set) => ({
  armed: false,
  lastActionAt: 0,
  helpOpen: false,

  arm: () => set({ armed: true, lastActionAt: performance.now() }),
  disarm: () => set({ armed: false }),
  toggleHelp: (open) => set((s) => ({ helpOpen: open ?? !s.helpOpen })),
  noteAction: () => set({ lastActionAt: performance.now() }),
}));

/**
 * Не пора ли погасить боевой режим.
 *
 * Проверяется НА НАЖАТИИ, а не по таймеру: таймер тикал бы вхолостую сутками, а
 * единственный момент, когда ответ важен, — это момент, когда клавиша тратит
 * деньги. Незамеченный переключатель, провисевший полчаса, — приглашение нажать
 * его не глядя.
 */
export function armExpired(armedSince: number, now: number): boolean {
  return now - armedSince > ARM_TIMEOUT_MS;
}

/** Приёмник действий. Ставит страница рынка, снимает при уходе. */
type Sink = (a: HotkeyAction) => void;

let sink: Sink | null = null;

export function setHotkeySink(fn: Sink | null): void {
  sink = fn;
}

let installed: (() => void) | null = null;

export function describeTarget(target: EventTarget | null): TargetInfo | null {
  if (target === null || !(target instanceof HTMLElement)) return null;
  // Явное приведение к boolean: isContentEditable реализован не везде (в jsdom
  // его нет вовсе), и undefined, просочившийся в TargetInfo, сделал бы поле
  // «булевым» только на бумаге. Правило от этого не сломалось бы — undefined
  // ложен, — но тип перестал бы описывать значение, а это ровно тот сорт
  // расхождения, который потом читают как «здесь точно boolean».
  return { tag: target.tagName, contentEditable: target.isContentEditable === true };
}

/**
 * Вешает слушатель на document. Идемпотентна.
 *
 * Приёмник вызывается ДАЖЕ вне боевого режима, и это структурное решение, а не
 * недосмотр: проверка режима живёт в приёмнике, чтобы панель могла сказать
 * «горячие клавиши не в боевом режиме». Молчаливое бездействие научило бы
 * оператора, что клавиша сломана, и обратное он узнал бы в худшую минуту.
 */
export function startHotkeys(): () => void {
  if (installed !== null) return installed;

  const onKeyDown = (e: KeyboardEvent) => {
    const binding = resolveHotkey(e);
    if (binding === null) return;

    const typing = isTyping(describeTarget(e.target));
    if (typing && !allowedWhileTyping(binding.action)) {
      return;
    }
    // preventDefault только для того, что мы реально обработали: «?» в поле
    // поиска обязан оставаться вопросительным знаком.
    e.preventDefault();

    // Подсказка и Escape — забота всего приложения, а не страницы рынка:
    // оператор, нажавший «?» на дашборде, обязан увидеть таблицу, а не тишину.
    switch (binding.action.type) {
      case "help":
        useHotkeys.getState().toggleHelp();
        return;
      case "escape":
        if (typing) {
          // В поле ввода Escape снимает фокус и НЕ трогает боевой режим:
          // наименее удивительное поведение из возможных.
          if (e.target instanceof HTMLElement) e.target.blur();
          return;
        }
        useHotkeys.getState().disarm();
        useHotkeys.getState().toggleHelp(false);
        return;
      default:
        sink?.(binding.action);
    }
  };

  document.addEventListener("keydown", onKeyDown);
  installed = () => {
    document.removeEventListener("keydown", onKeyDown);
    installed = null;
  };
  return installed;
}
